import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowRight, 
  ExternalLink,
  TrendingUp,
  Zap,
  BarChart3,
  Cpu,
  Heart,
  Microscope,
  ChevronRight,
  Clock,
  DollarSign,
  Target
} from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';

const UseCasesSection = () => {
  const [activeCase, setActiveCase] = useState(0);

  const useCases = [
    {
      id: 'energy',
      title: 'Energy: Oil & Gas Exploration',
      subtitle: 'Quantum QAOA + GPU Geophysics: Slashing Sub‑Surface Search Space',
      icon: Zap,
      category: 'Energy',
      gradient: 'from-yellow-600 to-orange-600',
      bgGradient: 'from-yellow-900/20 to-orange-900/20',
      description: 'Revolutionary approach combining quantum optimization with classical GPU computing for seismic inversion and drilling-site selection.',
      keyBenefits: [
        { label: 'Cost Reduction', value: '90%', icon: DollarSign },
        { label: 'Time Reduction', value: '18h → 2.5h', icon: Clock },
        { label: 'Hit Rate Increase', value: '8.3%', icon: Target }
      ],
      technicalHighlights: [
        'QAOA circuits on Rigetti Aspen‑M 80Q',
        'GPU-accelerated forward modeling',
        'Kubernetes orchestrated QPU sidecars',
        'Redis caching for parameter optimization'
      ],
      caseStudyUrl: 'https://dyota.substack.com/p/applied-quantum-computing-case-study',
      industry: 'Oil & Gas Exploration',
      implementation: 'Hybrid Classical-Quantum Pipeline',
      details: {
        challenge: 'Seismic inversion and drilling‑site selection require scanning vast combinatorial sets of sub‑surface parameters. Classical heuristic search with 64×H200 cluster costs USD 250k per run and delivers results in 18h—too slow for real-time decisions.',
        solution: 'Hybrid approach keeping coarse‑grained forward modeling on GPUs while off‑loading combinatorial subset selection to QAOA circuits running on Rigetti\'s Aspen‑M 80Q.',
        outcome: 'Search‑space pruned from 2.6 × 10⁶ combinations to top 50 candidate strata in 2.5h, achieving 90% cost savings with 8.3% higher hit rate in preliminary core samples.'
      }
    },
    {
      id: 'finance',
      title: 'Finance & Trading',
      subtitle: 'High‑Frequency Trading - Quantum Boltzmann Machines for Volatility Spike Prediction',
      icon: TrendingUp,
      category: 'Finance',
      gradient: 'from-emerald-600 to-green-600',
      bgGradient: 'from-emerald-900/20 to-green-900/20',
      description: 'Quantum-enhanced HFT systems using Quantum Boltzmann Machines for sub-30-second volatility prediction in NASDAQ futures.',
      keyBenefits: [
        { label: 'Sharpe Ratio', value: '+0.42', icon: BarChart3 },
        { label: 'Daily PnL', value: '+3.7%', icon: DollarSign },
        { label: 'Inference Time', value: '6.3ms', icon: Clock }
      ],
      technicalHighlights: [
        'Quantum Boltzmann Machine (QBM) sampler',
        'Rigetti Aspen‑M 128Q integration',
        'PyTorch hybrid neural networks',
        'Real-time limit-order-book analysis'
      ],
      caseStudyUrl: 'https://dyota.substack.com/p/applied-quantum-computing-in-finance',
      industry: 'High-Frequency Trading',
      implementation: 'Quantum-Classical Hybrid ML',
      details: {
        challenge: 'HFT desk needs to predict <30s volatility spikes on NASDAQ futures. GPU LSTM pipeline handles macro regime shifts but struggles with sudden micro‑flashes from cross‑venue order imbalances.',
        solution: 'Replace final LSTM attention head with Quantum Boltzmann Machine (QBM) sampler executed on Rigetti Aspen‑M 128Q to approximate partition function of limit‑order‑book state.',
        outcome: 'Back‑test on 30 days tick data shows Sharpe +0.42, daily pnl +3.7%. Live two‑week pilot confirmed +2.9% pnl vs baseline with P99 inference at 6.3ms.'
      }
    },
    {
      id: 'ai-infrastructure',
      title: 'AI Infrastructure',
      subtitle: 'Atlas Quantum Trading\'s 27% Alpha Generation with Quantum Sidecars',
      icon: Cpu,
      category: 'AI/ML',
      gradient: 'from-blue-600 to-purple-600',
      bgGradient: 'from-blue-900/20 to-purple-900/20',
      description: 'Revolutionary quantum sidecar architecture achieving unprecedented alpha generation through hybrid quantum-classical optimization.',
      keyBenefits: [
        { label: 'Annual Alpha', value: '$220M', icon: DollarSign },
        { label: 'Drawdown Reduction', value: '19%', icon: TrendingUp },
        { label: 'System Uptime', value: '99.9998%', icon: Target }
      ],
      technicalHighlights: [
        'Kubernetes KEDA auto-scaling QPU resources',
        'PyTorch quantum operators integration',
        'Rigetti Novera QPU with OPX+ control',
        'Cross-region quantum state replication'
      ],
      caseStudyUrl: 'https://dyota.substack.com/p/case-study-ai-realm-infrastructure',
      industry: 'Fintech Infrastructure',
      implementation: 'Quantum Kubernetes Sidecars',
      details: {
        challenge: 'Atlas Quantum Trading needed to solve combinatorial optimization problems in cross-asset correlated derivatives 30× faster than classical solvers while maintaining 99.9998% uptime across global data centers.',
        solution: 'Embedded Rigetti Novera QPU as Kubernetes-managed sidecars with KEDA auto-scaling, integrating quantum solutions into PyTorch-driven trading models with quantum-safe encryption.',
        outcome: 'Generated $220M/year alpha through quantum-accelerated arbitrage strategies, reduced portfolio drawdowns by 19%, and achieved 99.9998% system uptime across three global data centers.'
      }
    },
    {
      id: 'healthcare',
      title: 'Healthcare',
      subtitle: 'Quantum Drug Discovery & Molecular Simulation',
      icon: Heart,
      category: 'Healthcare',
      gradient: 'from-pink-600 to-red-600',
      bgGradient: 'from-pink-900/20 to-red-900/20',
      description: 'Accelerating pharmaceutical research through quantum-enhanced molecular simulation and drug discovery pipelines.',
      keyBenefits: [
        { label: 'Discovery Time', value: '70% Faster', icon: Clock },
        { label: 'Accuracy', value: '99.7%', icon: Target },
        { label: 'Cost Reduction', value: '60%', icon: DollarSign }
      ],
      technicalHighlights: [
        'Quantum chemistry algorithms',
        'Molecular dynamics simulation',
        'Variational Quantum Eigensolver (VQE)',
        'Protein folding optimization'
      ],
      caseStudyUrl: '#healthcare-case-study',
      industry: 'Pharmaceutical Research',
      implementation: 'Quantum Chemistry Suite',
      details: {
        challenge: 'Traditional drug discovery processes take 10-15 years and cost billions. Classical molecular simulation hits exponential scaling barriers for complex protein interactions.',
        solution: 'Quantum chemistry algorithms using VQE for ground state energy calculations, combined with quantum approximate optimization for molecular conformation searches.',
        outcome: 'Accelerated lead compound identification by 70%, achieved 99.7% accuracy in binding affinity prediction, and reduced computational costs by 60% compared to classical methods.'
      }
    },
    {
      id: 'research',
      title: 'Research',
      subtitle: 'Academic & Scientific Research Acceleration',
      icon: Microscope,
      category: 'Research',
      gradient: 'from-indigo-600 to-blue-600',
      bgGradient: 'from-indigo-900/20 to-blue-900/20',
      description: 'Empowering universities and research institutions with quantum computing capabilities for breakthrough scientific discoveries.',
      keyBenefits: [
        { label: 'Research Speed', value: '5× Faster', icon: Clock },
        { label: 'Problem Complexity', value: '1000× Larger', icon: Target },
        { label: 'Accuracy', value: '99.9%', icon: BarChart3 }
      ],
      technicalHighlights: [
        'Quantum simulation algorithms',
        'Materials science modeling',
        'Cryptography research',
        'Optimization problems'
      ],
      caseStudyUrl: '#research-case-study',
      industry: 'Academic Research',
      implementation: 'Research-as-a-Service Platform',
      details: {
        challenge: 'Academic institutions need access to cutting-edge quantum computing resources for research but lack the infrastructure and expertise to build quantum systems.',
        solution: 'Cloud-based quantum research platform providing easy access to quantum algorithms, simulation tools, and hybrid classical-quantum workflows.',
        outcome: 'Enabled 50+ universities to accelerate research by 5×, solve problems 1000× more complex than classical limits, and achieve 99.9% computational accuracy.'
      }
    }
  ];

  return (
    <section id="use-cases" className="py-24 bg-black relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_center,rgba(0,255,209,0.1),transparent_50%)]"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_center,rgba(99,102,241,0.05),transparent_70%)]"></div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <Badge className="mb-6 bg-emerald-500/10 text-emerald-300 border-emerald-500/20">
            Real-World Applications
          </Badge>
          <h2 className="display-medium mb-6 text-white">
            Quantum Computing Use Cases
          </h2>
          <p className="body-large text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Discover how Godquantum Systems is revolutionizing industries through practical quantum computing solutions. 
            From energy exploration to financial trading, our quantum-classical hybrid systems deliver measurable results.
          </p>
        </motion.div>

        {/* Use Cases Grid */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {useCases.map((useCase, index) => {
            const IconComponent = useCase.icon;
            const isActive = activeCase === index;
            
            return (
              <motion.div
                key={useCase.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                onClick={() => setActiveCase(index)}
                className="cursor-pointer"
              >
                <Card className={`p-6 h-full border transition-all duration-300 relative overflow-hidden group ${
                  isActive 
                    ? 'border-emerald-500/50 bg-emerald-500/5 shadow-2xl shadow-emerald-500/20' 
                    : 'border-white/10 bg-black/40 hover:border-emerald-500/30 hover:bg-emerald-500/5'
                }`}>
                  {/* Background Gradient */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${useCase.bgGradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                  
                  <div className="relative z-10">
                    {/* Category Badge */}
                    <div className="flex items-center justify-between mb-4">
                      <Badge className={`bg-gradient-to-r ${useCase.gradient} text-white border-none`}>
                        {useCase.category}
                      </Badge>
                      {isActive && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="w-3 h-3 bg-emerald-400 rounded-full"
                        />
                      )}
                    </div>

                    {/* Icon */}
                    <motion.div
                      className="mb-4"
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${useCase.gradient} flex items-center justify-center`}>
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                    </motion.div>

                    {/* Title & Description */}
                    <h3 className="heading-3 mb-3 text-white">{useCase.title}</h3>
                    <p className="text-gray-300 text-sm mb-4 leading-relaxed">
                      {useCase.description}
                    </p>

                    {/* Key Benefits */}
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      {useCase.keyBenefits.map((benefit, benefitIndex) => {
                        const BenefitIcon = benefit.icon;
                        return (
                          <div key={benefitIndex} className="text-center">
                            <BenefitIcon className="w-4 h-4 text-emerald-400 mx-auto mb-1" />
                            <div className="text-emerald-300 font-semibold text-xs">{benefit.value}</div>
                            <div className="text-gray-400 text-xs">{benefit.label}</div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Learn More */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">{useCase.industry}</span>
                      <ChevronRight className={`w-4 h-4 transition-colors ${
                        isActive ? 'text-emerald-400' : 'text-gray-500 group-hover:text-emerald-400'
                      }`} />
                    </div>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Detailed View */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeCase}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.6 }}
            className="bg-black/40 border border-white/10 rounded-3xl p-8 backdrop-blur-sm"
          >
            <div className="grid lg:grid-cols-2 gap-8 items-start">
              {/* Left Column - Details */}
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${useCases[activeCase].gradient} flex items-center justify-center`}>
                    {React.createElement(useCases[activeCase].icon, { className: "w-8 h-8 text-white" })}
                  </div>
                  <div>
                    <h3 className="heading-2 text-white">{useCases[activeCase].title}</h3>
                    <p className="text-emerald-300">{useCases[activeCase].implementation}</p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <h4 className="text-white font-semibold mb-2">Challenge</h4>
                    <p className="text-gray-300 text-sm leading-relaxed">
                      {useCases[activeCase].details.challenge}
                    </p>
                  </div>

                  <div>
                    <h4 className="text-white font-semibold mb-2">Solution</h4>
                    <p className="text-gray-300 text-sm leading-relaxed">
                      {useCases[activeCase].details.solution}
                    </p>
                  </div>

                  <div>
                    <h4 className="text-white font-semibold mb-2">Outcome</h4>
                    <p className="text-gray-300 text-sm leading-relaxed">
                      {useCases[activeCase].details.outcome}
                    </p>
                  </div>
                </div>
              </div>

              {/* Right Column - Technical & Actions */}
              <div className="space-y-6">
                {/* Key Benefits */}
                <div>
                  <h4 className="text-white font-semibold mb-4">Key Results</h4>
                  <div className="grid grid-cols-1 gap-3">
                    {useCases[activeCase].keyBenefits.map((benefit, index) => {
                      const BenefitIcon = benefit.icon;
                      return (
                        <div key={index} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg border border-white/10">
                          <BenefitIcon className="w-5 h-5 text-emerald-400" />
                          <div>
                            <div className="text-white font-semibold">{benefit.value}</div>
                            <div className="text-gray-400 text-sm">{benefit.label}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Technical Highlights */}
                <div>
                  <h4 className="text-white font-semibold mb-4">Technical Highlights</h4>
                  <div className="space-y-2">
                    {useCases[activeCase].technicalHighlights.map((highlight, index) => (
                      <div key={index} className="flex items-center gap-2 text-gray-300 text-sm">
                        <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
                        {highlight}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    className="btn-primary flex-1"
                    onClick={() => window.open(useCases[activeCase].caseStudyUrl, '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Read Full Case Study
                  </Button>
                  <Button 
                    className="btn-secondary flex-1"
                    onClick={() => {/* Open contact modal */}}
                  >
                    Discuss Your Use Case
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-r from-emerald-500/10 to-blue-500/10 border border-white/10 rounded-3xl p-8">
            <h3 className="heading-2 mb-4 text-white">Ready to Transform Your Industry?</h3>
            <p className="body-large text-gray-300 mb-6 max-w-2xl mx-auto">
              Discover how quantum computing can revolutionize your business operations. 
              Our team of quantum experts is ready to help you identify and implement quantum solutions.
            </p>
            <Button 
              className="btn-primary px-8 py-4"
              onClick={() => window.open('https://forms.gle/xy3W8pUjroDPVasx7', '_blank')}
            >
              Schedule a Quantum Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default UseCasesSection;